import { z } from 'zod';
import { commentAttachmentSchema } from './attachment';
import { ApiError } from './errors';
import { ulidSchema } from './id';
import { publicProfileSchema } from './user';

/**
 * Comments — the contract layer for block **B16**.
 *
 * `BE-16.1` (`FR-COLLAB-001`, post a comment and read the stream), `BE-16.2`
 * (`FR-COLLAB-002` edit your own, `FR-COLLAB-003` delete) and `BE-16.3`
 * (`FR-COLLAB-014`, the board's commenting permission). `FR-COLLAB-006` — how
 * the stream is ordered and paged — has **no `BE-` item** of its own and is
 * absorbed here, because ordering and paging are what `FR-COLLAB-001` AC4
 * ("another member with board access sees the comment") is delivered *through*.
 *
 * ---------------------------------------------------------------------------
 * THE FIRST UNBOUNDED COLLECTION IN THE PRODUCT, AND EVERY DECISION BELOW
 * FOLLOWS FROM THAT
 * ---------------------------------------------------------------------------
 * A card carries at most one description, a handful of members and a dozen
 * labels. It can carry ten thousand comments. That single fact decides three
 * things this file would otherwise have settled the other way:
 *
 *   1. **The card back is NOT extended.** `cardDetailSchema` in `./card-detail`
 *      is a *fixed-size projection* and stays one — no `comments` array, no
 *      `commentCount`. B14's members and B15's labels ride on it precisely
 *      because they are bounded; a comment array would make one card open an
 *      unbounded response. The stream is its own route,
 *      `GET /cards/:id/comments`.
 *   2. **The stream is paged, and paged by keyset rather than by offset.** See
 *      `commentStreamQuerySchema`.
 *   3. **`?limit` exists at all.** It is the **first query parameter in this
 *      product** — every shipped route declares `querystring:
 *      z.strictObject({})`, an empty strict object whose only job is to turn a
 *      guessed parameter into a 422 — and `COMMENT_PAGE_SIZE_MAX` exists
 *      because without a ceiling one request can pull every comment on a card.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE TWO PERMISSION FIELDS ON THE STREAM ARE BOTH LOAD-BEARING
 * ---------------------------------------------------------------------------
 * `commentStreamSchema` carries **`mayComment`** (a boolean, the server's
 * answer for *this viewer*) and **`commentingPermission`** (the board's
 * setting). Neither is convenience and they are not two spellings of one fact:
 * `mayComment` **decides**, `commentingPermission` only **words the message**.
 * `commentStreamSchema`'s docstring has the long form, including the state in
 * which a client that reverses the two says something false.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE DELIBERATELY DOES NOT DECLARE
 * ---------------------------------------------------------------------------
 *   - **A `source` or `externalSenderEmail` field.** Both columns exist and
 *     `ck_comments_source` admits `'email_reply'`, but `FR-COLLAB-031`
 *     (Parity-Later) owns them and B16 writes no route that produces either.
 *     Putting them on the wire would be a field with no writer — and an email
 *     address on a **public** board's comment stream is a disclosure decision
 *     that requirement must make when it lands. ⚠ The consequence, named as
 *     forward debt: an email-reply comment from a non-member would today
 *     serialise as `author: null`, indistinguishable from a deleted account.
 *     Unreachable now; the block that builds `FR-COLLAB-031` adds `source` and
 *     an external-sender projection, and **this schema is where**.
 *   - **Reactions.** `comment_reactions` is a real table B16 did not touch.
 *     B61 (`FR-COLLAB-013`) adds the small aggregate shape below and extends
 *     `commentSchema` with `reactions`.
 *   - **A mention shape.** `FR-COLLAB-007` is `B17`'s, and it writes
 *     `notifications` rather than anything here.
 *   - **A deleted/tombstone state.** `FR-COLLAB-003`'s deletion is permanent,
 *     `comments` has **no `deleted_at` column**, and the state is
 *     inexpressible by design. `DELETE /comments/:id` answers `204` with no
 *     body, so it needs no shape — `DELETE /lists/:id`'s and
 *     `DELETE /labels/:id`'s precedent.
 *   - **`commentCount` on a card.** `FR-CARD-034` AC4 wants a badge on the
 *     card front and there is no `BE-` item for it, so B16 does not build it;
 *     `boardFaceCardSchema` in `./board-contents` is untouched. The shape when
 *     somebody does is recorded in the block's design brief, along with the
 *     mechanical constraint that decides it (`comments` has no `board_id`
 *     column, so the grouping query must join through `cards`).
 *   - **A widening of `boardSchema`.** `commentingPermission` is **not** added
 *     to it. See `boardCommentingSchema`.
 *
 * The file follows the division every other feature file here follows —
 * **input primitives**, which validate and normalise what a route accepts, and
 * **views**, which describe what a route returns — plus a third section this
 * one needs and none of the others do: a **cursor codec**, the only pair of
 * functions in this package's feature files.
 */

// ---------------------------------------------------------------------------
// Caps and vocabularies — and the first of them is NOT a column width.
// ---------------------------------------------------------------------------

/**
 * The comment body cap — **`OQ-6`, answered by this block** and recorded in
 * `docs/requirements/03-collaboration.md`'s Open questions rather than only
 * here. Counted **after trimming**, because `commentBodySchema` trims before it
 * measures.
 *
 * ⚠ **DECLARED HERE AND DELIBERATELY NOT `CARD_DESCRIPTION_MAX_LENGTH`, EVEN
 * THOUGH BOTH ARE 16,384.** The authority is direct rather than inferred:
 * `./card-detail`'s own header names `comments.body` by name as one of the
 * columns that constant "must not be shared with", beside
 * `card_description_drafts.draft_text` and `boards.description`. The rule
 * `./card` set — *import the constant that describes YOUR column if one exists,
 * declare it if none does, and never share one across two columns* — exists
 * precisely so that a later block capping one cannot silently move the other.
 * Two columns that happen to agree today are still two product rules, and
 * `FR-CARD-003` (which fixes 16,384 for the description) says nothing whatever
 * about a comment.
 *
 * ⚠ **`comments.body` is `text`, so the database enforces NO length at all and
 * this schema is the ONLY cap in the system.** That is the same footing
 * `CARD_DESCRIPTION_MAX_LENGTH` stands on and it has the same consequence, and
 * the consequence is worth a sentence because it is the opposite of
 * `LABEL_NAME_MAX_LENGTH` one file over: losing *that* bound is a loud `value
 * too long for type character varying(255)` at INSERT, and losing **this** one
 * is caught **nowhere** — not by a column width, not by a CHECK, not by
 * `parity.sh`, not by any test that does not assert the number itself. It is
 * enforced on the way **in** only; a read never re-checks it, because a view
 * that enforced a product cap would refuse to serve a row the database
 * legitimately holds.
 *
 * 16,384 = 2^14, chosen to match what a comment composer can plausibly hold
 * rather than derived from a requirement — `FR-COLLAB-001` sets no cap, which
 * is why `OQ-6` was open. Counted in **UTF-16 code units**, because that is
 * what `z.string().max()` measures and what a browser's `textarea.value.length`
 * reports, so the server and the client's character counter agree without
 * either converting.
 */
export const COMMENT_BODY_MAX_LENGTH = 16384;

/**
 * How many comments `GET /cards/:id/comments` returns when the caller sends no
 * `?limit`.
 *
 * **No requirement fixes this number.** 25 is chosen so a card back's first
 * render needs one round trip on the overwhelming majority of cards, and it is
 * deliberately smaller than the ceiling so that the default is a *page* rather
 * than a de-facto "everything". ⚠ Tests must pass `?limit=` explicitly rather
 * than depending on this value: a fixture that relies on the default asserts
 * this constant instead of the behaviour it means to check, and moves when the
 * constant does.
 */
export const COMMENT_PAGE_SIZE_DEFAULT = 25;

/**
 * The ceiling on `?limit`.
 *
 * ⚠ **This exists because comments are the first unbounded collection in the
 * product.** Every other list this server serves is bounded by something real —
 * a board's lists, a list's cards on one board face, a card's members, a
 * board's labels — so no shipped route needed a ceiling and none has one.
 * Without this, `?limit=1000000` is a legal request that reads every comment on
 * a card into memory and serialises it, and the only thing standing between the
 * server and that is a client's good manners.
 *
 * 100 rather than 1,000: a "Load more" that fetches a hundred at a time is
 * already generous for a conversation, and the value that matters for
 * correctness is that there *is* one, not what it is.
 */
export const COMMENT_PAGE_SIZE_MAX = 100;

/**
 * `comment_reactions.emoji varchar(32) NOT NULL`, B61 (`FR-COLLAB-013`).
 *
 * Stored as the raw emoji string after NFC normalisation. There is deliberately
 * no shortcode vocabulary here: the boundary rejects values that are blank,
 * wider than the column, contain control/surrogate code points, or contain no
 * Unicode pictographic code point, and otherwise leaves the user's emoji
 * sequence alone.
 *
 * ⚠ **`COMMENT_EMOJI_SHORTCODES` below is a different vocabulary for a
 * different column, and the two must never be wired together.** That one is a
 * read-time rendering dictionary for `comments.body` (`FR-COLLAB-004`); this
 * one governs what `comment_reactions.emoji` may *store* (`FR-COLLAB-013`).
 * `docs/notes/B61-backend-plan.md` records the decision in as many words --
 * *"Do not introduce a shortcode vocabulary"* for reaction storage -- and it
 * stands. A reaction posted as `:tada:` is not a 🎉 reaction; it is a string
 * with no pictographic code point in it, and `reactionEmojiSchema` refuses it
 * on that ground, which is the ground it should be refused on. Cross-referenced
 * in both directions precisely because putting the two vocabularies in one
 * module is what makes the wiring look obvious.
 */
export const COMMENT_REACTION_EMOJI_MAX_LENGTH = 32;

/**
 * The revision of the canonical comment emoji shortcode vocabulary below.
 *
 * `1` is **the set that already shipped**, not a reset: `FE-30.1` moved the map
 * out of `apps/web/src/components/MarkdownBody.tsx` without adding or removing
 * a glyph, so version `1` names what the browser has been rendering all along.
 *
 * ⚠ **It exists because the vocabulary is APPEND-ONLY and a diff is the
 * only place drift can be caught.** See `COMMENT_EMOJI_SHORTCODES` clause (a):
 * removing or remapping an entry retroactively changes what every historical
 * comment says. Bump this when an entry is **added**; a removal or a remap is a
 * breaking change to stored conversation and needs an argument, not a bump.
 */
export const COMMENT_EMOJI_SHORTCODE_VERSION = 1;

/**
 * The canonical `:name:` emoji vocabulary for **comment bodies** --
 * `FR-COLLAB-004` ("Supported in comments: ... and `:shortcode:` emoji"), AC3
 * ("given I type `:tada:` and post, when the comment renders, then the
 * corresponding emoji glyph is shown"). `FE-30.1`.
 *
 * ---------------------------------------------------------------------------
 * WHAT WAS ACTUALLY WRONG, AND WHAT WAS NOT
 * ---------------------------------------------------------------------------
 * The gap this closes is **ownership, not size**. `MarkdownBody.tsx` held five
 * keys resolving to four glyphs (`tada`, `smile`, `heart`, `thumbs_up`, `+1`),
 * privately, where nothing else in the workspace could reach them -- so the
 * browser was the sole author of the vocabulary AND, less obviously, of the
 * token grammar. Both move here. The **glyph set does not grow**:
 * `FR-COLLAB-004` names exactly one shortcode, `:tada:`, and a scan of
 * `docs/requirements/` finds no requirement anywhere that names a second.
 * Shipping a full emoji table as a wire artefact would invent product from
 * nothing, which is the thing this repository's own rule about unsourced
 * clauses forbids. Four entries, four glyphs, one alias -- exactly what
 * shipped.
 *
 * ---------------------------------------------------------------------------
 * THE THREE CLAUSES THAT OUTRANK THE DATA ITSELF
 * ---------------------------------------------------------------------------
 * **(a) A read-time rendering dictionary, never a write-time transform -- and
 * therefore APPEND-ONLY.** `comments.body` is `text` and stores the raw source
 * (`:tada:` is stored as those six characters); there is no rendered column, no
 * shortcode column and no vocabulary table anywhere in the schema, and
 * `FR-COLLAB-004` AC4 -- *"when the editor loads, then I see my original
 * markdown source"* -- requires that it stay that way. Resolution happens on
 * every read. So **removing or remapping an entry rewrites what every
 * historical comment says**, silently, with nothing red anywhere. The
 * vocabulary grows by addition only, and
 * `COMMENT_EMOJI_SHORTCODE_VERSION` is what makes an addition visible.
 *
 * **(b) The server's opinion is the VOCABULARY, not a VALIDATOR. An unknown
 * `:name:` passes through literally, always.** This is the trap someone
 * "completing" the contract later walks into: `"meeting at 12:30:15"` matches
 * the grammar at `:30:`. It is harmless only because an unresolved name returns
 * the matched text unchanged. A shortcode check bolted onto
 * `createCommentBodySchema` would **422 a timestamp** -- so that schema is not
 * touched, and must not be. Nothing in the database constrains a `:` either:
 * `ck_comments_body_not_blank` trims and requires length and has no opinion
 * about the character at all. The server's silence about which tokens are
 * *legal* is structural; its opinion about which are *canonical* is this array.
 *
 * **(c) The resolver runs on RENDERED INLINE CHILDREN, never on the raw
 * markdown.** `code` and `pre` are absent from the browser's comment component
 * map, so `:tada:` inside backticks or a fenced block stays **literal** --
 * which is `FR-COLLAB-004` AC2's *"no markdown interpreted inside it"*. Now
 * that the resolver is a shared function, the tempting simplification is to run
 * it once over the source before parsing, which would silently eat shortcodes
 * inside code with nothing red anywhere. It is a rendering pass over already
 * rendered text, and the call site stays where it is.
 *
 * ---------------------------------------------------------------------------
 * SHAPE NOTES
 * ---------------------------------------------------------------------------
 * ⚠ **No Zod schema, deliberately.** Nothing validates a shortcode name
 * anywhere -- clause (b) is the reason -- so a schema here would exist only to
 * be misused as the validator that must not exist. Data-and-function exports in
 * this file have precedent: `COMMENT_AUTHOR_PRESENCES`,
 * `COMMENTING_PERMISSIONS`, `COMMENT_BODY_MAX_LENGTH`, `encodeCommentCursor`.
 * Nothing here is a field on a response shape; **no schema in this module is
 * widened by this vocabulary, in any way.**
 *
 * ⚠ **`aliases` is always an array, `[]` where there is none**, rather than
 * an optional field, so no consumer branches on presence. The
 * canonical-name/alias split is *derived* from what shipped -- `thumbs_up` and
 * `+1` both resolved to 👍 -- and is not an invention: `+1` is the alias
 * because `thumbs_up` is the name the vocabulary is keyed by.
 *
 * ⚠ **It is never fetched.** Public-board comment reading is
 * anonymous-capable (`FR-PERM-010` AC1 names comments in as many words), so a
 * catalogue the browser had to request would put a call on a read path that has
 * no session. This is module data in a package every client already imports;
 * an anonymous reader and the board's owner render the same four bytes in the
 * same render pass. That is also why no `GET /comments/emoji-shortcodes`
 * exists: every client in this workspace, `apps/mcp` included, can `import` it.
 */
export const COMMENT_EMOJI_SHORTCODES = [
  { name: 'tada', emoji: '🎉', aliases: [] },
  { name: 'smile', emoji: '😄', aliases: [] },
  { name: 'heart', emoji: '❤️', aliases: [] },
  { name: 'thumbs_up', emoji: '👍', aliases: ['+1'] },
] as const satisfies readonly {
  name: string;
  emoji: string;
  aliases: readonly string[];
}[];

/**
 * One entry of the vocabulary, **derived from the data rather than declared
 * beside it** -- the same rule that makes every type in this package a
 * `z.infer` of its schema rather than a second hand-written copy that can
 * drift.
 *
 * ⚠ **It is a union of the four LITERAL entries, not a general entry shape --
 * `name` is `'tada' | 'smile' | 'heart' | 'thumbs_up'` -- and that is
 * intended.** A consumer can therefore switch over the vocabulary
 * exhaustively, and cannot **construct** an entry: the set is closed and
 * append-only (clause (a)), so this module is the only place a shortcode may
 * be authored, and the type says so. A consumer that only needs to render
 * comment text wants `replaceCommentEmojiShortcodes` and never this type at
 * all. If a future consumer genuinely needs to hold an arbitrary entry, widen
 * *there* with `{ name: string; emoji: string; aliases: readonly string[] }`
 * rather than loosening the vocabulary's own type, which is what makes the
 * closure checkable.
 */
export type CommentEmojiShortcode = (typeof COMMENT_EMOJI_SHORTCODES)[number];

/**
 * Resolve one `:name:` to its glyph, or `undefined` if the vocabulary does not
 * know it. Canonical names and aliases resolve identically.
 *
 * ⚠ **Case-insensitive, because it always was.** The shipped browser code
 * lower-cased the captured name before looking it up, so `:TADA:` renders 🎉
 * today; that behaviour moves here intact rather than being tightened on the
 * way. Canonical names in `COMMENT_EMOJI_SHORTCODES` are lower-case by
 * construction, which is what makes the single `.toLowerCase()` sufficient.
 *
 * `undefined` rather than the input, so a caller can tell "not a shortcode"
 * from "a shortcode that resolves to this text" -- clause (b) lives or dies on
 * that distinction.
 */
export function resolveCommentEmojiShortcode(name: string): string | undefined {
  const wanted = name.toLowerCase();
  for (const entry of COMMENT_EMOJI_SHORTCODES) {
    if (entry.name === wanted) {
      return entry.emoji;
    }
    for (const alias of entry.aliases) {
      if (alias === wanted) {
        return entry.emoji;
      }
    }
  }
  return undefined;
}

/**
 * Replace every recognised `:name:` in `text` with its glyph, leaving
 * everything else -- including every unrecognised `:name:` -- byte-identical.
 *
 * ⚠ **`string` in, `string` out, and that is load-bearing rather than
 * stylistic.** The browser calls this and then hands the result to the mention
 * segmenter, which takes a string; a resolver that returned rich nodes would
 * silently break the mention-chip path. It is a text pass, and it stays one.
 *
 * ⚠ **This function owns the GRAMMAR, which was as private as the map.**
 * `/:([a-z0-9_+-]+):/gi` is the shipped pattern character for character -- the
 * `+` and `-` in the class are the only reason `:+1:` matches at all, and
 * dropping either would retire an alias the vocabulary still lists. The regex
 * is built inline per call and deliberately **not** exported as a module-level
 * `const`: a shared `/g` regex carries `lastIndex`, so a caller that reached
 * for `.test()` on it would get alternating answers.
 *
 * ⚠ **An unknown token is returned as it was matched** -- clause (b). This
 * is what keeps `"meeting at 12:30:15"` a timestamp instead of a 422 or a
 * mangled string, and any future change that makes an unmatched token anything
 * other than itself is a change to what stored comments say.
 */
export function replaceCommentEmojiShortcodes(text: string): string {
  return text.replace(/:([a-z0-9_+-]+):/gi, (match, name: string) => {
    return resolveCommentEmojiShortcode(name) ?? match;
  });
}

/**
 * Every value `boards.commenting_permission` accepts — `FR-COLLAB-014`'s five
 * options, which the column has carried since `0002_boards_lists_cards.sql`.
 *
 * The order below is **`ck_boards_commenting_permission`'s own**, read off the
 * live constraint rather than reconstructed, and it is also the requirement's
 * own order — narrowest first, each option *adding* to the one before it.
 * Keeping the two lists in the same order is what makes a drift between them
 * visible in a diff rather than only in a failing INSERT; that is
 * `LABEL_COLOURS`' stated rule one table over, and the integration suite
 * asserts this array against the live constraint's member list for the same
 * reason `labelColourSchema` is asserted against `ck_labels_colour`.
 *
 * ⚠ **The list is a ladder, not a set of unrelated modes.** `FR-COLLAB-014`
 * words each option as an addition — "Members and Observers (**adds**
 * observers)", "Workspace members (**adds** any member of the board's
 * workspace)" — so `disabled ⊂ members ⊂ members_and_observers ⊂
 * workspace_members ⊂ any_user`, and a board member does not lose the right to
 * comment when the setting is widened past `members`. The server encodes that
 * as `COMMENTING_ADMITS` in `apps/server/src/boards/permissions.ts`, oriented
 * caller-class-first; this array is the vocabulary, not the mapping.
 *
 * ⚠ **`disabled` refuses board admins too.** `FR-COLLAB-014` AC2 is verbatim
 * "given commenting is set to Disabled, when **any user including a board
 * admin** opens a card, then no composer is rendered". **The setting is not an
 * admin override**, which is the one cell in the five nobody guesses, because
 * every other board setting in this product *is* one.
 *
 * ⚠ **`any_user` is "offered only on public boards"** — a constraint on
 * *setting* the value as well as on honouring it. The server refuses
 * `any_user` on a non-public board with a 409 naming the remedy, and leaves an
 * already-stored `any_user` alone when a board is later made private (the guard
 * fails closed on its own). Both halves are argued in
 * `apps/server/src/routes/comments.ts`; **this schema accepts all five values
 * on the way in**, because the refusal is a fact about the board's current
 * state rather than about the shape of the body, and a boundary that rejected
 * it would answer 422 for something the caller can fix by changing a different
 * setting.
 */
export const COMMENTING_PERMISSIONS = [
  'disabled',
  'members',
  'members_and_observers',
  'workspace_members',
  'any_user',
] as const;

// ---------------------------------------------------------------------------
// INPUT primitives — what a route ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * A comment body as `comments.body` stores it — trimmed, non-empty, at most
 * `COMMENT_BODY_MAX_LENGTH`.
 *
 * ⚠ **IT TRIMS, AND THE STORED BODY IS THE TRIMMED VALUE — THE OPPOSITE OF
 * `cardDescriptionPatchSchema`.** Lead with the requirement, because the
 * structural argument alone would not settle it: `FR-COLLAB-001`'s behaviour
 * sentence is "An empty or **whitespace-only** comment cannot be posted", and
 * AC2 makes it observable — "given the composer is empty, when I attempt to
 * submit, then submission is rejected and **no activity entry is created**".
 *
 * The structural half is what decides *where* that refusal happens. A comment
 * has **no clear-gesture and no NULL state**: `comments.body` is `NOT NULL` and
 * `ck_comments_body_not_blank CHECK (char_length(trim(body)) > 0)` refuses
 * blank at the column, so there is nothing for "empty after trim" to mean
 * except a 422 at the boundary. ⚠ **Without `.trim()` BEFORE `.min(1)`, a
 * whitespace-only body passes this schema and hits that CHECK as a 500 rather
 * than a 422** — the wrong status code, on `FR-COLLAB-001` AC2's own case, and
 * a failure nothing in a green suite would show.
 *
 * `.trim().min(1).max()` is `boardTitleSchema`'s, `listTitleSchema`'s,
 * `cardTitleSchema`'s, `labelNameSchema`'s and `bioSchema`'s chain, reused
 * rather than re-derived — those five are the precedent that the chain
 * validates on the **trimmed** value, so no Zod semantics need asserting here
 * beyond the boundary cases.
 *
 * ⚠ **The contrast with the description must not be "harmonised", and the two
 * sit one requirement apart.** `cardDescriptionPatchSchema` deliberately does
 * **not** trim: a description is Markdown, four leading spaces are an indented
 * code block, two trailing spaces are a hard line break, and it has an explicit
 * *clear* gesture that maps to `NULL`. Neither applies here. Comments are
 * Markdown too (`FR-COLLAB-004`, V1), so the cost is real — and it is bounded
 * and accepted: `FR-COLLAB-004`'s supported set lists **fenced** code blocks
 * and not indented ones, so trimming the ends of a comment removes nothing the
 * requirement promises to render, while it removes the only way
 * `ck_comments_body_not_blank` could ever fire.
 */
export const commentBodySchema = z.string().trim().min(1).max(COMMENT_BODY_MAX_LENGTH);

const PICTOGRAPHIC = /\p{Extended_Pictographic}/u;
const CONTROL_OR_SURROGATE = /[\p{Cc}\p{Cs}]/u;

const storedReactionEmojiSchema = z
  .string()
  .max(COMMENT_REACTION_EMOJI_MAX_LENGTH)
  .refine((value) => value.trim().length > 0, 'Emoji is required')
  .refine(
    (value) => !CONTROL_OR_SURROGATE.test(value),
    'Emoji must not contain control characters',
  )
  .refine((value) => PICTOGRAPHIC.test(value), 'Emoji must contain an emoji');

export const reactionEmojiSchema = z
  .string()
  .transform((value) => value.normalize('NFC'))
  .pipe(storedReactionEmojiSchema);

export type ReactionEmoji = z.infer<typeof reactionEmojiSchema>;

/**
 * `POST /cards/:id/comments` — `FR-COLLAB-001` AC1.
 *
 * ⚠ **No `cardId`.** The card is the path segment, so a body `cardId` beside a
 * path `:id` would be two spellings of one fact that can disagree, and the
 * route would have to decide which wins. `createLabelBodySchema` makes the
 * identical call for the same reason; `createListBodySchema` and
 * `createCardBodySchema` carry a parent id only because `POST /lists` and
 * `POST /cards` have no path segment to disagree with.
 *
 * ⚠ **No `source`.** `comments.source` is `NOT NULL DEFAULT 'composer'` and
 * `ck_comments_source` also admits `'email_reply'`, but the value is a fact the
 * *route* asserts rather than one the caller may claim — `FR-COLLAB-031`'s
 * future inbound-email route asserts the other one. A caller who could send
 * `source: 'email_reply'` could forge provenance on a public board.
 *
 * ⚠ **No `watch` flag, and posting writes no `watches` row.**
 * `FR-COLLAB-001` AC3 offers the commenter the option to watch the card, and
 * `FR-COLLAB-024`'s edge case is explicit that auto-watching is the **rejected**
 * product decision ("Trello instead prompts the commenter to watch"). Watching
 * is `FR-COLLAB-022`/`FR-COLLAB-023` and has no route yet, so AC3 is owed by
 * `FE-16.2` rather than served here — and a `watch: true` key added "while we
 * are in the body" would silently multiply `B17`'s notification volume with
 * nothing failing.
 *
 * ⚠ **Two identical wrapper objects rather than one shared body**, this and
 * `commentBodyPatchSchema`. Deliberate: `POST` will grow `FR-COLLAB-005`'s
 * attachment reference and `FR-COLLAB-031`'s `source`, and `PATCH` must never
 * grow either. One shared object would make that divergence a breaking change
 * to both routes at once.
 */
export const createCommentBodySchema = z.strictObject({
  body: commentBodySchema.describe(
    'The comment text as Markdown, up to 16,384 characters after trimming ' +
      '(OQ-6). Leading and trailing whitespace IS stripped and the trimmed ' +
      'value is what is stored — an empty or whitespace-only comment is ' +
      'refused with a 422 (FR-COLLAB-001), because a comment has no “clear” ' +
      'gesture and no null state to collapse to.',
  ),
});

export type CreateCommentBody = z.infer<typeof createCommentBodySchema>;

/**
 * `PATCH /comments/:id/body` — `FR-COLLAB-002`. SC-4, field-scoped.
 *
 * The body moves on its own and can move nothing else — **never a
 * `PUT /comments/:id`**. That is barely a restriction here, because the body is
 * the only mutable column on the row: `created_at` is fixed, `card_id` and
 * `author_user_id` are identity, `source` is provenance, and `edited_at` is
 * derived from the act of editing. Which is exactly why the rule needs stating
 * — a `PUT` would look harmless on this table and would make
 * `FR-COLLAB-002`'s "keeps its original position and original posted-at time"
 * expressible in a body.
 *
 * ⚠ **Editing is an AUTHORSHIP check, not a permission check, and the two are
 * separate.** `FR-COLLAB-002` grants the author the right "at any time, with no
 * time limit" and AC3 refuses everybody else *including a board admin* ("given
 * I am a board admin and another member authored a comment, when I open its
 * action menu, then Edit is still not offered"). The commenting ladder
 * (`COMMENTING_PERMISSIONS`) governs *posting*; it is not consulted here, so an
 * author can still edit a comment on a board whose commenting has since been
 * turned off. See `apps/server/src/routes/comments.ts`.
 *
 * ⚠ **An edit to identical text still emits `comment.edited` and still moves
 * `edited_at`.** That looks inconsistent beside the no-op suppression B14 and
 * B15 built, and it is B14's stated boundary rather than an oversight: those
 * suppress a **toggle** two clients can race, and this is an explicit **Save**
 * under last-write-wins, which settles who wrote last even when the text did
 * not change. `card.description_edited`'s docstring in `./events` draws the same
 * line for the same reason.
 */
export const commentBodyPatchSchema = z.strictObject({
  body: commentBodySchema.describe(
    'The replacement comment text, same rules as on creation. Required — an ' +
      'edit with no body says nothing — and not nullable, because clearing a ' +
      'comment is spelt DELETE /comments/:id, which is permanent and leaves no ' +
      'tombstone (FR-COLLAB-003).',
  ),
});

export type CommentBodyPatch = z.infer<typeof commentBodyPatchSchema>;

// ---------------------------------------------------------------------------
// The commenting setting — FR-COLLAB-014, BE-16.3.
// ---------------------------------------------------------------------------

/**
 * `boards.commenting_permission varchar(32) NOT NULL DEFAULT 'members'`,
 * `ck_boards_commenting_permission`.
 *
 * `z.enum` over the vocabulary, so an unknown value is a **422 naming the
 * field** rather than a `check_violation` surfacing as a 500 —
 * `boardVisibilitySchema`'s and `labelColourSchema`'s call for the same class
 * of column (a CHECK-constrained `varchar`).
 *
 * ⚠ **The enum is used on the view as well as on the input**, which is
 * `labelSchema.colour`'s precedent rather than `publicProfileSchema.bio`'s. The
 * distinction is whether an out-of-vocabulary value is *stale data a reader
 * should tolerate* or *inexpressible in the column*: a bio over its cap can
 * exist because the column is wider than the product rule, and a sixth
 * commenting permission cannot, because the CHECK refuses it at INSERT. So a
 * value here that fails to parse means the constraint and
 * `COMMENTING_PERMISSIONS` have drifted, which is something a read should
 * surface rather than pass through.
 */
export const commentingPermissionSchema = z.enum(COMMENTING_PERMISSIONS);

export type CommentingPermission = z.infer<typeof commentingPermissionSchema>;

/**
 * `PATCH /boards/:id/commenting-permission` — `FR-COLLAB-014`. SC-4.
 *
 * ⚠ **All five values are accepted here, and there is NO asymmetry between the
 * patch and the view** — unlike `boardVisibilitySchema`, which admits four
 * levels while its patch accepts three because `enterprise_visible` is storable
 * and unsettable. `ck_boards_commenting_permission` and
 * `COMMENTING_PERMISSIONS` are the **same five values**, which is what makes
 * the integration suite's comparison against the live constraint a meaningful
 * check rather than a tautology.
 *
 * ⚠ **`any_user` is accepted by this schema and may still be refused by the
 * route, with a 409 rather than a 422.** `FR-COLLAB-014` AC5 ("given a board is
 * private, when I open the commenting permission options, then 'Any user' is
 * not offered") is a client rule about what the menu **offers**; the server rule
 * is about what it will **accept**, and what refuses the caller is the board's
 * current `visibility` — a fact about state, which is what a 409 means in this
 * codebase (`assertBoardIsOpen`, `assertBoardRetainsAdmin`) and what the caller
 * can fix by making the board public first. A client that hides an option is
 * not an enforcement of it; `isAddableBoardMember` makes the identical argument
 * against `FR-CARD-008` AC4.
 */
export const commentingPermissionPatchSchema = z.strictObject({
  commentingPermission: commentingPermissionSchema.describe(
    'One of the five FR-COLLAB-014 options, narrowest first: disabled, ' +
      'members, members_and_observers, workspace_members, any_user. Each adds ' +
      'to the one before it. “disabled” refuses everybody INCLUDING board ' +
      'admins (AC2). “any_user” is accepted here but refused with a 409 on a ' +
      'board that is not public, because that is a fact about the board’s ' +
      'state rather than about this body.',
  ),
});

export type CommentingPermissionPatch = z.infer<typeof commentingPermissionPatchSchema>;

/**
 * What `PATCH /boards/:id/commenting-permission` answers with.
 *
 * ⚠ **`boardSchema` IS NOT WIDENED, and this is the one place B16 deliberately
 * diverges from B09's shape.** `PATCH /boards/:id/visibility` answers with
 * `boardSchema` because `boardSchema` carries `visibility`; the parallel move
 * here would be to add `commentingPermission` to it — which changes **eight**
 * shipped responses (`GET /boards/:id`, both short-link resolvers,
 * `GET /workspaces/:id/boards`, the title patch, the visibility patch,
 * close/reopen, and `boardContentsSchema.board`).
 *
 * It is rejected for the reason this block files the route by **subject**
 * rather than by path prefix: a route in `comments.ts` must not have to import
 * `boards.ts`'s private `BOARD_COLUMNS` and `toBoard` to answer, which is
 * exactly the cross-module coupling `board-contents.ts` refused. This narrow
 * shape is what keeps the module self-contained, and the client that needs the
 * setting on the card back gets it from `commentStreamSchema`, where it is
 * needed anyway.
 *
 * ⚠ **The matching read now exists and reuses THIS shape, with `mayComment`
 * kept OFF it.** `GET /boards/:id/commenting-permission` — which `FR-COLLAB-014`
 * AC4 needs so a *normal* member can see the control without being able to edit
 * it, and which `FR-COLLAB-016` needs so a client can **word** the locked
 * composer — was delivered by **B30** (`BE-30.2`) in `apps/server/src/routes/
 * comments.ts`, as an unowned gap rather than as B16 work reopened. One shape,
 * two routes, nothing to drift.
 *
 * `mayComment` stays off it, and that is the whole difference between this
 * response and `commentStreamSchema`'s: that field is the **viewer's** answer
 * and belongs on the stream; a per-viewer field on a per-board response is how
 * two clients holding one cached object start disagreeing about what it means.
 * `apps/server/test/unit/FR-COLLAB-016.spec.ts` pins the two-key shape from the
 * schema side, and the integration suite pins it again on the raw body — a
 * widening here would otherwise be invisible, since `z.object` strips the extra
 * key rather than rejecting it.
 */
export const boardCommentingSchema = z.object({
  boardId: ulidSchema,
  commentingPermission: commentingPermissionSchema,
});

export type BoardCommenting = z.infer<typeof boardCommentingSchema>;

/**
 * One aggregate reaction chip for a comment.
 *
 * `count` is the durable count of rows for this emoji. `users` is the public
 * tooltip projection for users that can still be joined; deleted users can
 * therefore make `count > users.length`. `reactedByMe` is computed for the
 * current viewer and is false for anonymous callers.
 */
export const commentReactionSchema = z.object({
  emoji: storedReactionEmojiSchema,
  count: z.number().int().min(1),
  reactedByMe: z.boolean(),
  users: z.array(publicProfileSchema),
});

export type CommentReaction = z.infer<typeof commentReactionSchema>;

export const commentReactionListSchema = z.array(commentReactionSchema);

export type CommentReactionList = z.infer<typeof commentReactionListSchema>;

// ---------------------------------------------------------------------------
// Views — what a route RETURNS.
//
// Width-bounded or unbounded strings, no format checks, no transforms — see
// `./user`'s longer version of the same argument. `commentingPermission` is the
// exception and stays an enum; its docstring says why.
// ---------------------------------------------------------------------------

/**
 * One comment, as `POST /cards/:id/comments`, `PATCH /comments/:id/body` and
 * every element of `GET /cards/:id/comments` return it.
 *
 * One view for all three, rather than a narrower shape per route, because they
 * all answer the same question — *what is this comment now* — and a client that
 * has just posted one needs the same fields as one that has just read the
 * stream. `labelSchema`'s and `listSchema`'s argument, one table over.
 */
export const commentSchema = z.object({
  id: ulidSchema,
  /**
   * `comments.card_id char(26) NOT NULL`, `fk_comments_card_id` **ON DELETE
   * CASCADE**.
   *
   * On the wire although the stream is already card-scoped, because a client
   * holding a comment out of context — a mention notification, a live
   * `comment.added` arriving on the board room — needs to know which card back
   * to route it to.
   *
   * ⚠ There is deliberately **no `boardId`**. The board is recoverable from the
   * card, and the consumer that genuinely needs it without a lookup is the live
   * one, which reads the **event** rather than this shape — and every B16 event
   * carries `boardId` on a column.
   */
  cardId: ulidSchema,
  /**
   * `comments.number integer NOT NULL`, `uq_comments_card_id_number` — the
   * comment's number **on its card** (owner-directed 2026-10-13; migration
   * `0068`, `docs/notes/comment-numbers.md`). The first comment on a card is
   * `1`, the next `2`, and so on.
   *
   *   - **Assigned by the database**, by `trg_comments_assign_number` from the
   *     `comment_counters` high-water mark, on every insert path. No write
   *     route accepts it and no caller can choose it.
   *   - **Never reused.** Deleting a comment leaves a gap (`#1, #2, #4`); the
   *     next comment takes the next number, not the freed one.
   *   - **Not an ordering key.** Under concurrent posts on one card the number
   *     order can differ from `createdAt` order; the stream still orders by
   *     `(createdAt, id)`.
   *   - **For humans to cite (`#3`).** `id` stays the identity every route and
   *     tool takes.
   *
   * ⚠ Widened IN PLACE, deliberately, against the usual "extend a shipped
   * schema on a copy" rule: every consumer of this schema — POST, PATCH, the
   * stream (`commentStreamCommentSchema`), batch-read and the activity entry's
   * `.comment` — is a view of a comment, and all of them should carry it. No
   * route that must NOT carry it serialises this schema (`cardShareCommentSchema`
   * is separate and strict).
   */
  number: z.number().int().positive(),
  /**
   * `comments.body text NOT NULL`.
   *
   * ⚠ **`z.string()` with NO `.max()`.** The cap is a **write** boundary
   * (`COMMENT_BODY_MAX_LENGTH`); a view that enforced it would refuse a row the
   * column legitimately holds, and `comments.body` is `text`, so a row over the
   * cap is not merely conceivable — nothing in the database prevents one. That
   * is the exact call `publicProfileSchema.bio` and
   * `cardDetailSchema.description` already make, and the failure mode if it were
   * bounded here is a **500 on a read** for data that arrived by any path other
   * than this package.
   */
  body: z.string(),
  /**
   * Who wrote it, or `null` when nobody can be named.
   *
   * `publicProfileSchema.nullable()` — reused whole, exactly as
   * `cardMemberSchema` extends it, so a comment's attribution cannot acquire an
   * `email` by accident and a field added to a public profile appears
   * everywhere at once. `FR-COLLAB-001` AC1 asks for "my avatar, my name",
   * which is `avatarUrl` and `displayName` plus `initials`/`initialsColour` for
   * the fallback avatar — all of which the base already carries.
   *
   * ⚠ **Three rules decide what a comment by an absent person serialises as,
   * and getting any of them wrong is SILENT:**
   *
   *   1. **The `users` join is `LEFT`, never `INNER`.** `comments.author_user_id`
   *      is nullable with `ON DELETE SET NULL`, so an INNER join **drops the
   *      comment from the stream entirely** — a 200 with a shorter array, no
   *      error anywhere, and a direct violation of `FR-COLLAB-003`'s promise
   *      that deletion is the only way a comment leaves the stream.
   *   2. **No `deleted_at` / `deactivated_at` filter on the author, and no join
   *      to `board_members` at all.** `FR-COLLAB-006` AC4 is the requirement:
   *      "given a comment author has **left the board**, when I read their
   *      comment, then their name still renders with a deactivated-style avatar
   *      and the comment is not removed." A comment's attribution is a fact
   *      about who wrote it, not about who is currently on the board —
   *      `readCardMembers` made the same call first.
   *   3. **`null` is the only "no author" state and the CLIENT renders it.**
   *      The server invents no placeholder name: a server-side fallback string
   *      would be an untranslatable sentence built by concatenation, which
   *      `FR-COLLAB-018`'s edge case forbids by name.
   */
  author: publicProfileSchema.nullable(),
  /**
   * `comments.created_at timestamptz(3) NOT NULL DEFAULT now()`.
   *
   * ⚠ **A bounded-below string and emphatically NOT `z.iso.datetime()`.**
   * Verified rather than assumed: `boardSchema`, `listSchema`, `cardSchema`,
   * `labelSchema`, `workspaceSchema` and `cardDetailSchema` all spell a
   * `timestamptz(3)` this way, and every one of their docstrings names
   * `z.iso.datetime()` as the **rejected** alternative in as many words — `pg`
   * hands a `timestamptz` back in Postgres' own spelling
   * (`2026-08-12 12:00:00.123+00`, a **space** rather than a `T`, no `Z`),
   * which is a legitimate instant and not an ISO 8601 form. An
   * `z.iso.datetime()` here would make this the only field in the package that
   * refuses the value the database actually produced, and the failure is a
   * **500 on every comment read**, not a compile error.
   *
   * ⚠ **An edit must not touch it.** `FR-COLLAB-002`: an edited comment "keeps
   * its original position and original posted-at time in the stream". There is
   * no trigger to get this wrong, so it goes wrong only by a handler adding
   * `createdAt` to its `set` — after which the comment silently jumps to the
   * top of the stream, which reads as a *feature* to anyone not holding the
   * requirement.
   */
  createdAt: z.string().min(1),
  /**
   * `comments.edited_at timestamptz(3) NULL` — `null` until the comment is
   * edited, and `FR-COLLAB-002`'s "(edited)" marker is exactly
   * `editedAt !== null`.
   *
   * ⚠ **THIS COLUMN HAS NO TRIGGER AND THE HANDLER MUST WRITE IT.** That is the
   * block's sharpest inversion and it is the opposite of a rule the rest of this
   * codebase teaches: `cards.updated_at` and `labels.updated_at` are owned by
   * `trg_cards_set_updated_at` / `trg_labels_set_updated_at`, and this
   * repository names "a second writer of a trigger-derived column" as a mistake
   * three times over. **`comments` has no `updated_at` column and no trigger at
   * all** — measured against the live catalogue — so a developer who has
   * internalised "never write the timestamp" omits `edited_at` from the `set`,
   * the route answers 200 with the new body, and the only symptom is that
   * "(edited)" never appears anywhere.
   *
   * ⚠ **The timestamp is on the wire and there is NO derived `edited` boolean.**
   * That is deliberately the *opposite* of `listSchema.archived` and
   * `boardSchema.closed`, which derive a boolean and hide the timestamp, and the
   * difference is that this timestamp is itself renderable: `FR-COLLAB-006` puts
   * an absolute timestamp on hover, and "edited 2h ago" is a thing a client
   * shows. One field, one fact — do **not** add both.
   *
   * ⚠ It remembers only the **last** edit. The per-edit record is the
   * `comment.edited` activity entry, which is the only reason that event is
   * written at all given that it does not surface in the feed.
   */
  editedAt: z.string().min(1).nullable(),
  /**
   * `comment_attachments`, resolved for the CALLER of the route that answers
   * this shape — **block `B27`, `FR-COLLAB-005`, `BE-27.4`.** Added
   * additively; every field above this one is byte-identical to the shape
   * `POST /cards/:id/comments`, `GET /cards/:id/comments` and `PATCH
   * /comments/:id/body` answered before B27, and the array is filled by ONE
   * grouped statement per response (`apps/server/src/comments/attachments.ts`)
   * rather than a query per comment — never N+1.
   *
   * ⚠ **This field is PER VIEWER, and that is new on this shape.** Every
   * other field on `commentSchema` renders identically for anyone who may
   * read the comment at all; a `card_link` or `board_link` entry inside this
   * array does not — `commentAttachmentSchema`'s own module header (see
   * `./attachment`) argues why a card or board a relation names may sit on a
   * board this particular reader cannot open, and such an entry resolves to
   * a placeholder carrying only an id, computed FRESH on every read rather
   * than stored. Two callers of this schema reading the SAME comment at the
   * same instant can therefore see two different arrays.
   *
   * ⚠ **`[]` is the ordinary state for every comment predating this block**,
   * exactly as `cardDetailSchema.checklists` reads for a card predating
   * `B26` — there is no backfill and none is owed. `FR-COLLAB-005` AC4
   * ("deleting the card's attachment from the attachment section" leaves the
   * comment showing it as REMOVED) is satisfied by a `kind: 'file'` entry
   * whose `attachment` field is `null`, never by the entry disappearing from
   * this array: `comment_attachments` rows are never deleted by that gesture
   * (only their `attachment_id` is nulled, `fk_comment_attachments_
   * attachment_id ON DELETE SET NULL`), so the array's LENGTH does not
   * change and a client counting entries sees the same count before and
   * after.
   */
  attachments: z.array(commentAttachmentSchema),
  /**
   * `comment_reactions`, grouped by emoji for the CALLER of the route that
   * answers this shape - B61, `FR-COLLAB-013`.
   *
   * Like `attachments`, this is per-viewer state on the comment view:
   * `reactedByMe` differs by caller, and `[]` is the ordinary state for a
   * comment with no reactions or for historical comments predating this block.
   */
  reactions: commentReactionListSchema,
});

export type Comment = z.infer<typeof commentSchema>;

/**
 * The **author-presence** vocabulary — `FR-COLLAB-006` AC4, `FE-16.2`.
 *
 * AC4 is verbatim: *"Given a comment author has left the board, when I read
 * their comment, then their name still renders with a **deactivated-style
 * avatar** and the comment is not removed."* Rendering that treatment needs
 * a fact the comment stream has never carried — `commentSchema.author` is
 * `publicProfileSchema.nullable()` and says nothing about whether the person
 * is still around.
 *
 * ⚠ **ONE NULLABLE ENUM RATHER THAN TWO BOOLEANS, and the precedence is part
 * of the contract**: `deactivated` **outranks** `departed`. A deactivated
 * account is an account-level fact and is therefore true on *every* board at
 * once, so a member who both deactivated and left reads `deactivated`. Two
 * booleans would buy a precedence-free wire at the cost of a second render
 * branch on a surface where both states render identically — AC4 asks for one
 * treatment, not two.
 *
 * - `active` — the author is a live account and a current member of the card's
 *   board.
 * - `departed` — a live account that is **no longer a member of this board**.
 *   `FR-COLLAB-018` AC4 says their name and avatar must still resolve, so this
 *   is emphatically **not** a reason to withhold the profile.
 * - `deactivated` — `users.deactivated_at IS NOT NULL`.
 *
 * ⚠⚠ **A PRIVACY BOUNDARY AS MUCH AS A STYLING ONE, and the boundary is drawn
 * one state EARLIER than this enum.** A **deleted** account is not a value
 * here: `fk_comments_author_user_id` is `ON DELETE SET NULL`, so a deleted
 * account already serialises as `author: null` and this field is `null` beside
 * it — no name, no former display name, no email, nothing to style. For the
 * two states that *are* here, the profile keeps rendering, and that is
 * required rather than tolerated: withholding it would violate
 * `FR-COLLAB-006` AC4 and `FR-COLLAB-018` AC4 both, and it would disclose
 * nothing anyway, because `publicProfileSchema` carries no email by
 * construction.
 *
 * ⚠ **This is a FLAG and never a FILTER.** `commentSchema.author`'s own rules
 * 1 and 2 stand unchanged: the `users` join stays `LEFT`, there is still no
 * `deactivated_at` filter, and the `board_members` lookup that computes
 * `departed` is an `EXISTS`, never an `INNER JOIN`. Turning either into a
 * conjunct drops the comment from the stream with a 200 and a shorter array —
 * the exact failure `FR-COLLAB-003` forbids, arriving by another route.
 */
export const COMMENT_AUTHOR_PRESENCES = ['active', 'departed', 'deactivated'] as const;

export const commentAuthorPresenceSchema = z.enum(COMMENT_AUTHOR_PRESENCES);

export type CommentAuthorPresence = z.infer<typeof commentAuthorPresenceSchema>;

/**
 * A comment as the **stream** returns it — `GET /cards/:id/comments`,
 * `POST /cards/:id/comments` and `PATCH /comments/:id/body` — carrying two
 * attribution fields the base view does not.
 *
 * ⚠⚠ **AN EXTENSION ON A COPY, AND THE COPY IS FORCED BY A FILE THIS BLOCK
 * DOES NOT OWN — it is not a taste call.** Four sites serialise
 * `commentSchema`. Three are the comment routes; the fourth is
 * `activityEntrySchema.comment`, which serves `GET /cards/:id/activity`,
 * `GET /boards/:id/activity` and `GET /workspaces/:id/activity`, and whose
 * values are **hand-built object literals** in
 * `apps/server/src/activity/hydrate.ts` (`readCommentsById` builds a
 * `Map<string, Comment>` field by field). A required field added to the base
 * would make those literals a `tsc` error in three shipped activity routes,
 * and the fix would have to happen in a module with no business knowing about
 * board membership. Extending on a copy is also the house rule stated
 * generally — *one schema serialises several routes, so widening it in place
 * changes responses that must not carry the field* — and it is
 * requirement-faithful besides: `FR-COLLAB-006` is titled "Present the comment
 * **stream** consistently", and these two fields are exactly what the stream
 * needs and the feed does not.
 *
 * ⚠ **Both fields are `.optional()`, and that is MEASURED rather than
 * preferred.** They were first written plainly nullable and `pnpm typecheck`
 * was run; `apps/web/src/lib/comments.ts` — not this block's file — declares
 * `createComment`/`updateComment` as `Promise<Comment>` and then does
 * `comments: [comment, ...stream.comments]`, which required fields on the
 * element type make illegal. `.optional()` is also *honest* about what that
 * client holds: `z.object` **strips**, so a row it reconciled from a mutation
 * response parsed with base `commentSchema` genuinely lacks them. **The routes
 * send both fields on every response regardless.** The residual for whoever
 * owns `lib/comments.ts` next is to parse the two mutation responses with this
 * schema and drop the `.optional()`.
 */
export const commentStreamCommentSchema = commentSchema.extend({
  /**
   * Whether the author is still around — `FR-COLLAB-006` AC4, `FE-16.2`. See
   * `COMMENT_AUTHOR_PRESENCES` for the vocabulary, the precedence and why this
   * is a flag rather than a filter.
   *
   * ⚠ **`null` EXACTLY WHEN `author` IS `null`, and the two move together.**
   * A deleted account leaves `author: null` through `ON DELETE SET NULL`, and
   * there is then no person to describe; a presence value beside a null author
   * would be a state no query can produce and no client can render.
   */
  authorPresence: commentAuthorPresenceSchema.nullable().optional(),
  /**
   * The automation or integration that posted this comment, by name — or
   * `null` for one a person typed (or emailed in). `FE-70.4`,
   * `FR-AUTO-016` AC3.
   *
   * ⚠⚠ **THE VALUE COMES FROM `activity_entries.actor_automation_name`, AND
   * THE LIVE CATALOGUE IS WHAT SETTLES THAT — a `comments` column for it would
   * be a migration.** `comments.source` is `varchar(32) NOT NULL DEFAULT
   * 'composer'` under `ck_comments_source CHECK (source IN ('composer',
   * 'email_reply'))` — **there is no automation value in the constraint**, and
   * `apps/server/src/automation/execute.ts` accordingly inserts an
   * automation's comment with `source: 'composer'`. The automation's identity
   * rides only on the `comment.added` event emitted beside it
   * (`actorAutomationName: input.commandName`), which lands in
   * `activity_entries`. So the provenance of an automated comment exists in
   * exactly one table, and this field is a join to it — resolved in ONE batched
   * statement per page over `payload->>'commentId'`, never a lookup per row.
   *
   * ⚠ **`author` stays populated beside it, and that is `FR-AUTO-016` AC3
   * rather than an oversight.** AC3 is verbatim: *"the comment appears under
   * the acting member's name and avatar and is **distinguishable from a
   * comment they typed**."* Both halves are required — the name AND the
   * distinction — so `author !== null && via !== null` is the ordinary shape of
   * an automation-posted comment. A client that renders one instead of the
   * other fails half of AC3.
   *
   * Rendered through `ACTIVITY_VIA_TEMPLATE` (`'{sentence}, via {tool}'`) in
   * `./activity`, by the client. The server composes **no** sentence:
   * `FR-COLLAB-018`'s edge case forbids server-side concatenation by name,
   * because the result is untranslatable.
   *
   * Bare `z.string()` with no `.max()`, matching
   * `activityEntrySchema.actorAutomationName` exactly — the same
   * `varchar(255)` column read through two views, and an asymmetric bound
   * between them is a 500 on whichever is narrower.
   */
  via: z.string().nullable().optional(),
});

export type CommentStreamComment = z.infer<typeof commentStreamCommentSchema>;

// ---------------------------------------------------------------------------
// Paging — the cursor, its codec, and the query.
//
// ⚠ THE ONLY FUNCTIONS IN ANY FEATURE FILE IN THIS PACKAGE. They live here
// rather than in the server because the client must be able to hold a cursor
// and hand it back, and because `comment.test.ts` can then assert the
// round trip and the refusals without a database.
// ---------------------------------------------------------------------------

/**
 * An opaque paging cursor. Bounded, not structured — a client reads it from
 * `commentStreamSchema.nextCursor` and hands it back verbatim.
 *
 * 128 is comfortably above what `encodeCommentCursor` produces (a Postgres
 * timestamp plus a ULID plus a separator is 53 bytes, so 71 base64url
 * characters) and low enough that a fabricated cursor cannot be used to push a
 * megabyte through a query string.
 *
 * ⚠ **Bounded here and *decoded* separately.** This schema only says "a
 * plausible cursor-shaped string"; `decodeCommentCursor` is what refuses a
 * malformed one, and it must be called — see its docstring, which explains why
 * base64 decoding cannot be trusted to fail on garbage.
 */
export const commentCursorSchema = z.string().min(1).max(128);

/**
 * `GET /cards/:id/comments`'s query string.
 *
 * ⚠ **`z.strictObject`, and here it has teeth beyond the usual.** A plain
 * `z.object()` in Zod 4 **strips** unknown keys, so a typo'd `?curser=` would
 * answer 200 having silently ignored it — and because this parameter is what
 * advances a "Load more", the visible symptom is a button that re-serves page
 * one for ever. The strict form makes it a 422 naming the key.
 *
 * ⚠ **`z.coerce.number()` is untested ground in this repository — this is the
 * first query parameter in the product — so the ordering was MEASURED rather
 * than assumed.** Under Zod 4 the coercion runs **before** `.int()`, `.min()`
 * and `.max()`, so the bounds are applied to the number and not to the string's
 * length. The measured outcomes, pinned in `comment.test.ts` because a client
 * sees the difference: `?limit=50` → `50`; `?limit=0` → `too_small`;
 * `?limit=101` → `too_big`; `?limit=abc` → `invalid_type` (`Number('abc')` is
 * `NaN`); `?limit=` → `too_small` (`Number('')` is `0`, **not** a type error);
 * `?limit=1.5` → `invalid_type` (from `.int()`). If a future Zod release
 * reorders this, the spelling that certainly works is
 * `z.string().regex(/^\d+$/).transform(Number).pipe(z.number().int().min(1).max(…))`
 * — the test is what will tell you.
 *
 * ⚠ **`.default()` is evaluated before the coercion**, so an omitted `?limit`
 * yields `COMMENT_PAGE_SIZE_DEFAULT` rather than `Number(undefined)`, which is
 * `NaN`. Also measured, not assumed.
 */
export const commentStreamQuerySchema = z.strictObject({
  cursor: commentCursorSchema
    .optional()
    .describe(
      'An opaque cursor from a previous response’s nextCursor. Omit it for the ' +
        'first (newest) page. A malformed cursor is a 422, never a silent ' +
        'restart from the top.',
    ),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(COMMENT_PAGE_SIZE_MAX)
    .default(COMMENT_PAGE_SIZE_DEFAULT)
    .describe(
      'How many comments to return, 1 to 100, default 25. Coerced from the ' +
        'query string, so the bounds apply to the number rather than to the ' +
        'string’s length.',
    ),
});

export type CommentStreamQuery = z.infer<typeof commentStreamQuerySchema>;

/**
 * The decoded cursor: the exact `(created_at, id)` pair of the last comment on
 * the page that was served.
 *
 * Module-private on purpose — a caller receives this shape from
 * `decodeCommentCursor` and never constructs one from a cursor by hand.
 * `createdAt` is bounded at 64 rather than left free so that a fabricated
 * cursor cannot smuggle a long string into a query parameter; a
 * `timestamptz(3)` renders as about 26 characters.
 */
const commentCursorPayloadSchema = z.strictObject({
  createdAt: z.string().min(1).max(64),
  id: ulidSchema,
});

/**
 * The base64url alphabet, unpadded.
 *
 * ⚠ **Hand-rolled rather than `btoa`/`atob` or `Buffer`, and both alternatives
 * were rejected for measured reasons rather than taste.** `Buffer` is Node-only
 * and this package is bundled into `apps/web` by Vite, which does not polyfill
 * it. `btoa`/`atob` exist at runtime in Node and in every browser but are
 * **not declared** under this workspace's `lib: ["ES2023"]` with no DOM lib —
 * `tsc` answers `TS2304: Cannot find name 'btoa'`, verified before writing
 * this. Twenty lines with no platform dependency is the cheaper answer than
 * widening `lib` for two functions.
 */
const BASE64URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Latin-1 (here: ASCII) in, unpadded base64url out. */
function toBase64Url(input: string): string {
  let out = '';
  for (let i = 0; i < input.length; i += 3) {
    const b0 = input.charCodeAt(i);
    const b1 = input.charCodeAt(i + 1);
    const b2 = input.charCodeAt(i + 2);
    const has1 = !Number.isNaN(b1);
    const has2 = !Number.isNaN(b2);
    const triple = (b0 << 16) | ((has1 ? b1 : 0) << 8) | (has2 ? b2 : 0);
    out += BASE64URL_ALPHABET.charAt((triple >>> 18) & 63);
    out += BASE64URL_ALPHABET.charAt((triple >>> 12) & 63);
    if (has1) out += BASE64URL_ALPHABET.charAt((triple >>> 6) & 63);
    if (has2) out += BASE64URL_ALPHABET.charAt(triple & 63);
  }
  return out;
}

/**
 * Unpadded base64url in, latin-1 out. Throws on a character outside the
 * alphabet and on a length that no base64 string can have (`length % 4 === 1`).
 *
 * ⚠ It does **not** throw on every string that merely fails to mean anything —
 * `'abc'` decodes to two bytes of noise quite happily — which is precisely why
 * `decodeCommentCursor` validates the *shape* afterwards rather than treating a
 * successful decode as proof of anything.
 */
function fromBase64Url(input: string): string {
  if (input.length % 4 === 1) throw new Error('not base64url');
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const character of input) {
    const value = BASE64URL_ALPHABET.indexOf(character);
    if (value < 0) throw new Error('not base64url');
    // Masked to sixteen bits deliberately. Only the low twelve are ever read
    // back (`bits` never exceeds twelve), and leaving the accumulator to grow
    // would run it past thirty-two bits, where JavaScript's bitwise operators
    // truncate — harmlessly here, but only by an argument a reader would have
    // to reconstruct.
    buffer = ((buffer << 6) | value) & 0xffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((buffer >>> bits) & 0xff);
    }
  }
  return out;
}

/**
 * Turn the last comment on a page into the cursor that fetches the next one.
 *
 * ⚠ **The cursor is VALUE-CARRYING and self-contained — base64url of
 * `` `${createdAt}|${id}` `` — and emphatically NOT `?before=<commentId>`.**
 * Two reasons, both mechanical:
 *
 *   1. **`FR-COLLAB-003`'s hard delete would dangle a row-naming cursor.** A
 *      client paging through a stream from which the boundary comment is
 *      deleted between pages would send an id that resolves to nothing, and the
 *      route would have to choose between a 404 on a "Load more" and silently
 *      restarting at the top. A value-carrying cursor decodes **without a row
 *      lookup** — note that this function's signature takes no database handle,
 *      which is the property, not an accident — and keeps working against a
 *      deleted boundary.
 *   2. **`B17` wants the identical contract.** `FR-NOTIF-006`'s drawer pages
 *      backwards over a different table with a different sort key; an opaque
 *      cursor lets it adopt `?cursor` + `?limit` + `nextCursor: string | null`
 *      with different internals, so no client code changes shape. ⚠ It declares
 *      its **own** codec rather than importing this one: a shared codec would
 *      couple two tables' sort keys, and the day one changes the other changes
 *      silently. **The shape is the reusable thing, not the function.**
 *
 * ⚠ **`createdAt` is carried as the exact string the driver returned**, in
 * Postgres' spelling (`2026-08-12 12:00:00.123+00`) rather than an ISO one —
 * see `commentSchema.createdAt`. That is why the route's `WHERE` casts it
 * (`$cursorCreatedAt::timestamptz`).
 *
 * ⚠ **This docstring used to call that cast "load-bearing rather than
 * defensive", on the grounds that a text-versus-`timestamptz` comparison would
 * be resolved against the text form and order `'…09:…'` after `'…10:…'`. That
 * is MEASURED FALSE and is corrected in place rather than deleted, because the
 * cast stays and the reason changed.** Against `molt_v1_probe`: with the
 * parameter sent untyped (`unknown`, which is what `pg` sends) Postgres infers
 * `timestamptz` from the row comparison's left-hand side and the answer is
 * correct; with the parameter sent as `text` it raises `operator does not
 * exist: timestamp with time zone < text`, a **500** rather than a silent
 * mis-ordering. Text ordering *would* be wrong for an unpadded hour, but
 * Postgres always renders a padded one, so that failure is unreachable. The
 * cast is therefore defence against the driver's parameter typing changing
 * under the route — and the only thing that makes the comparison's type visible
 * to a reader. `apps/server/src/routes/comments.ts` carries the same
 * correction beside the query.
 *
 * ⚠ **Encode from the last row KEPT, never from the extra `limit + 1`th row**
 * the query fetched to discover whether another page exists. From the extra
 * row, every page boundary silently skips exactly one comment.
 */
export function encodeCommentCursor(row: { createdAt: string; id: string }): string {
  const raw = `${row.createdAt}|${row.id}`;
  // Guard the one assumption the codec makes. Both halves are ASCII by
  // construction (a Postgres timestamp and a Crockford base32 ULID), so this
  // can only fire if a caller invents a row — in which case failing loudly here
  // beats emitting a cursor that decodes to mojibake.
  for (let i = 0; i < raw.length; i += 1) {
    if (raw.charCodeAt(i) > 0xff) {
      throw new Error('comment cursor components must be latin-1');
    }
  }
  return toBase64Url(raw);
}

/**
 * Turn a cursor back into the `(created_at, id)` pair it names.
 *
 * ⚠ **A MALFORMED CURSOR IS A 422, NEVER "START FROM THE TOP".** Silently
 * falling back to the first page turns a "Load more" into an infinite loop that
 * re-serves page one for ever — a 200 with a well-formed body, so nothing
 * anywhere fails. That is why this function throws an
 * `ApiError('validation_failed')` rather than a plain `Error` a route must
 * remember to catch: the rejected alternative leaves the refusal a matter of
 * handler discipline, and a forgotten `try` is a **500** instead of the 422 the
 * requirement wants.
 *
 * ⚠ **The shape check after the decode is the whole defence, because base64
 * decoding does not fail on garbage.** `'abc'` is legal base64url and decodes
 * to two bytes of noise; without the checks below, the route would build
 * `WHERE (created_at, id) < ('<noise>'::timestamptz, '<noise>')` and Postgres
 * would answer with a 500 on `invalid input syntax for type timestamp with time
 * zone` — which is the exact failure this function exists to prevent. So all
 * four of these are refusals: a character outside the alphabet, an impossible
 * length, a decoded string that does not split into **exactly two** parts on
 * `|`, and a second part that is not a ULID.
 *
 * `|` is safe as the separator because neither component can contain one — a
 * `timestamptz` renders as digits, hyphens, colons, a dot, a space and a sign,
 * and Crockford base32 is `[0-9A-HJKMNP-TV-Z]`.
 */
export function decodeCommentCursor(cursor: string): { createdAt: string; id: string } {
  const invalid = (): never => {
    throw new ApiError('validation_failed', 'Malformed cursor', {
      cursor: ['must be a cursor returned by a previous page of this stream'],
    });
  };

  let raw: string;
  try {
    raw = fromBase64Url(cursor);
  } catch {
    return invalid();
  }

  const parts = raw.split('|');
  if (parts.length !== 2) return invalid();

  const parsed = commentCursorPayloadSchema.safeParse({
    createdAt: parts[0],
    id: parts[1],
  });
  if (!parsed.success) return invalid();

  return parsed.data;
}

// ---------------------------------------------------------------------------
// The stream.
// ---------------------------------------------------------------------------

/**
 * `GET /cards/:id/comments` — `FR-COLLAB-001` AC4 and `FR-COLLAB-006`.
 *
 * ⚠ **An object rather than a bare array**, which diverges from
 * `labelListSchema` and `listListSchema` and is not a house-style lapse: this
 * response carries three things beside the rows — the next cursor, the board's
 * setting and the viewer's answer — and a bare array has nowhere to put them.
 *
 * **Ordering is newest first**, `ORDER BY comments.created_at DESC,
 * comments.id DESC`. `FR-COLLAB-001` AC1 puts a new comment "at the **top** of
 * the stream" and `FR-COLLAB-006` says "comments and activity render newest
 * first". ⚠ **The `id DESC` tie-break is not decoration**: `created_at` is
 * `timestamptz(3)` defaulted to `now()`, which inside a transaction is the
 * *transaction* timestamp, so two comments posted in the same millisecond — or
 * two seeded in one fixture — are a genuine tie that Postgres may return in any
 * order it likes. `comments.id` is the primary key, so `(created_at, id)` is a
 * **total** order; without it a page boundary can duplicate or skip a row with a
 * 200 and a well-formed body. That is `rank.ts` rule 2 applied to a table with
 * no rank.
 *
 * ⚠ **Reading the stream requires the board READ scope only, never comment
 * permission.** `FR-COLLAB-014` AC2 is explicit that when commenting is
 * Disabled "existing comments remain readable", so conflating the two would
 * make this block's own AC2 fail with a 403 that looks principled. The route is
 * anonymous-capable for the same reason `FR-PERM-010` AC1 names comments in as
 * many words: a signed-out visitor to a public board "can read lists, cards
 * **and comments** but sees no edit affordances".
 */
export const commentStreamSchema = z.object({
  /**
   * Newest first. `[]` is a legitimate answer and means the card has none.
   *
   * ⚠ **`commentStreamCommentSchema`, not `commentSchema` — the extension, not
   * the base.** `FE-16.2` and `FE-70.4` need `authorPresence` and `via` on the
   * stream. The element type is a strict superset of what this array parsed
   * before, so a client that knew only the base sees exactly what it saw.
   *
   * ⚠⚠ **THIS ARRAY IS NO LONGER THE ONLY CONSUMER OF THE EXTENSION, AND THE
   * SENTENCE THAT ONCE SAID IT MUST BE — corrected here rather than left to
   * contradict a sibling.** This docstring used to read that the two fields
   * *"must not put them on `activityEntrySchema.comment`"*. **That prohibition is
   * withdrawn**: `../activity.ts`'s `activityEntrySchema.comment` **is** a
   * `commentStreamCommentSchema` as of `FE-70.4`, and the reasoning that once
   * forbade it was wrong on one fact — the card back renders its comments out of
   * the **activity feed**, not out of this stream (`CommentSection.tsx`), so
   * `FR-COLLAB-006` AC4's deactivated-author treatment is a *feed* obligation as
   * much as a stream one. Carrying the fields only here forced a second, clamped
   * read at `COMMENT_PAGE_SIZE_MAX`, past which every older comment rendered with
   * **no presence at all** and a 200. That read is deleted.
   *
   * ⚠ **What survives from the old sentence, and is the reason the base is still
   * the base:** a **required** field on `commentSchema` would turn every
   * hand-built comment literal in `apps/server/src/activity/hydrate.ts` into a
   * `tsc` error across four shipped activity routes. Both fields stay
   * `.optional()`, which is what let the feed repoint at this schema with zero
   * construction-site repairs — and is also why `tsc` alone will not catch a
   * projection that stops populating them. See `BE-61.8`.
   */
  comments: z.array(commentStreamCommentSchema),
  /**
   * The cursor for the next (older) page, or `null` at the end of history.
   *
   * ⚠ **`null` when there is no `limit + 1`th row — NOT "when the page was
   * short".** Emitting a cursor whenever the page came back full gives a "Load
   * more" that returns an empty page at every exact multiple of `limit`, which
   * a client renders as a button that does nothing. `FR-COLLAB-006` AC3 and
   * `FR-NOTIF-006`'s drawer both need the end-of-history state to be
   * unambiguous, and this field is it.
   */
  nextCursor: commentCursorSchema.nullable(),
  /**
   * The board's `commenting_permission`, as stored.
   *
   * ⚠ **It WORDS the message; it does not DECIDE.** `FR-COLLAB-016` AC1
   * requires a specific string — *"Commenting is turned off on this board"* —
   * distinct from *"you need to be a board member"*, so the client needs the
   * **setting** to choose the wording. It cannot be used to compute whether the
   * viewer may comment, because the ladder also needs workspace membership and
   * board visibility, neither of which any card-back response carries.
   *
   * ⚠ **The state where a client that reversed the two says something false:**
   * a board that held `any_user` and was later made **private** keeps the
   * stored value (nothing rewrites it, and the guard fails closed on its own),
   * so this field reads `any_user` on a private board. A client wording the
   * lock from this field alone will tell a stranger they may comment.
   */
  commentingPermission: commentingPermissionSchema,
  /**
   * Whether **this viewer** may post on this card right now — `false` for an
   * anonymous caller, always.
   *
   * ⚠ **This and the `POST` guard must be ONE predicate, not two spellings.**
   * The route evaluates the ladder **in SQL on the board row** and reads back a
   * boolean; it never re-implements the mapping in TypeScript and never fetches
   * `commenting_permission` and branches on it. If the two ever disagree the
   * symptom is two 200s that contradict each other — the composer renders
   * enabled and the `POST` answers 403, or worse the reverse — with no status
   * code anywhere to say so.
   *
   * Projecting a decision the database made is not "filtering in application
   * code" (SC-5): the row is already scoped, and the five-way mapping never
   * leaves SQL.
   */
  mayComment: z.boolean(),
});

export type CommentStream = z.infer<typeof commentStreamSchema>;

// ---------------------------------------------------------------------------
// The stream as `GET /cards/:id/comments` serves it since the batch-reads
// change — `docs/notes/batch-reads.md`, "The stream with `after`".
//
// ⚠ BOTH ARE COPIES, AND THE TWO ORIGINALS ABOVE ARE DELIBERATELY UNWIDENED.
// `apps/web/src/lib/comments.ts` builds its query from
// `commentStreamQuerySchema.partial()` and parses responses with
// `commentStreamSchema`, a plain `z.object`, so the web strips the new keys on
// parse and its cache updaters never see them; `comment.test.ts` pins the
// original's keys unchanged. The route's querystring and 200 schema are the
// copies, and so is the batch entry's `comments`.
// ---------------------------------------------------------------------------

/**
 * `GET /cards/:id/comments`'s query string with `after`.
 *
 * ⚠ **Still strict** — Zod 4's `.extend` keeps the strict catchall, and
 * `comment.test.ts` asserts it on this copy, because a stripped `?after` would
 * page from the top for ever and answer 200.
 *
 * ⚠ **`after` with `cursor` is a 422, and that rule is NOT here.** It is thrown
 * by the route handler. A `.refine` on this schema would be lost silently by the
 * MCP tool, which builds its input from `.shape`.
 */
export const commentStreamReadQuerySchema = commentStreamQuerySchema.extend({
  after: ulidSchema
    .optional()
    .describe(
      'A comment id. Returns only comments created after it, OLDEST first, ' +
        'capped by limit, plus editedSinceAfter: older comments edited since it. ' +
        'Cannot be combined with cursor (422). A gap-free after is a ' +
        'latestCommentId from this route or a commentId from a comment.added ' +
        'event; an id that names no comment on this card falls back to the ' +
        'instant the id encodes, minus a minute, and may repeat comments.',
    ),
});

export type CommentStreamReadQuery = z.infer<typeof commentStreamReadQuerySchema>;

/**
 * `GET /cards/:id/comments`'s 200, and a `POST /cards/batch-read` entry's
 * `comments`. `docs/notes/batch-reads.md`, "Response" and "The horizon".
 */
export const commentStreamReadSchema = commentStreamSchema.extend({
  /**
   * More comments exist beyond this page in the direction being read. Newest
   * first: exactly `nextCursor !== null`. After mode: call again at once with
   * `after = latestCommentId`.
   */
  hasMore: z.boolean(),
  /**
   * The newest comment this answer covered that is safe to store as the next
   * `after` — see "The horizon". After mode: the newest new comment returned,
   * else `after` echoed. Newest first, first page: the newest returned comment
   * older than the commit-safe horizon, else `null`. A cursor page: `null`,
   * because an older page establishes no position.
   */
  latestCommentId: ulidSchema.nullable(),
  /**
   * After mode only — older comments edited since the anchor was posted (the
   * anchor itself included when it was edited), most recently edited first.
   * ⚠ Absent, never `[]`, outside after mode: an empty array would assert
   * "nothing was edited", which a newest-first read never asked. And no
   * `edited: true` boolean: an edited comment is marked by being here, with its
   * `editedAt` (`toComment`'s "one field, one fact").
   */
  editedSinceAfter: z.array(commentStreamCommentSchema).optional(),
  /**
   * After mode only — `editedSinceAfter` overflowed `limit`. It cannot be paged
   * (there is no edit cursor): read the stream newest first instead.
   */
  editedSinceAfterHasMore: z.boolean().optional(),
});

export type CommentStreamRead = z.infer<typeof commentStreamReadSchema>;

// ---------------------------------------------------------------------------
// One comment by its per-card number — `GET /cards/:id/comments/by-number/:number`
// (owner request 2026-10-14, `docs/notes/comment-numbers.md`, "Lookup by
// number"). The MCP tool `molt_get_comment` wraps it.
// ---------------------------------------------------------------------------

/**
 * The largest `comments.number` the column can hold — it is a Postgres
 * `integer` under `ck_comments_number CHECK (number > 0)`. The web's
 * `parseCommentNumber` (`c.$shortLink.com.$number.tsx`) applies the same bound.
 */
export const COMMENT_NUMBER_MAX = 2_147_483_647;

/**
 * A comment number as it arrives in a path segment: a positive decimal integer
 * with no sign, no leading zero, no exponent and no whitespace, at most
 * `COMMENT_NUMBER_MAX`, parsed to a number.
 *
 * ⚠ **Not `z.coerce.number()`.** Coercion accepts `03`, `1e2`, `" 3"` and
 * `0x10`, so two spellings would name one comment and a citation could not be
 * compared as text. ⚠ **The upper bound is load-bearing**: without it
 * `9999999999` reaches Postgres as an `integer` overflow and answers 500, not
 * 422.
 */
export const commentNumberParamSchema = z
  .string()
  .regex(/^[1-9][0-9]{0,9}$/, 'must be a positive integer with no leading zero')
  .transform(Number)
  .pipe(z.number().int().positive().max(COMMENT_NUMBER_MAX))
  .describe(
    'The comment’s number on this card, as people cite it (#3): the card’s ' +
      'first comment is 1, each later one the next, never reused.',
  );

/**
 * `GET /cards/:id/comments/by-number/:number`'s params. A new schema rather
 * than a widening of the stream route's `{ id }`: the parameter is still named
 * `id` at that depth, because a different name under `/cards/:…/comments` is a
 * boot-time router collision (`apps/server/src/app.ts`).
 */
export const commentByNumberParamsSchema = z.object({
  id: ulidSchema,
  number: commentNumberParamSchema,
});

export type CommentByNumberParams = z.infer<typeof commentByNumberParamsSchema>;

/**
 * `GET /cards/:id/comments/by-number/:number`'s 200 — **exactly one entry of
 * `GET /cards/:id/comments`'s `comments`**, the same schema by reference (an
 * alias, not a copy and not a widening). It already carries `cardId` and
 * `number`, so a caller holding only this answer can cite and route it.
 *
 * ⚠ **No `mayComment` or `commentingPermission`.** Those describe the card's
 * composer, not a comment, and a caller that needs them reads the stream.
 */
export const commentByNumberSchema = commentStreamCommentSchema;

export type CommentByNumber = z.infer<typeof commentByNumberSchema>;

/**
 * `GET /cards/:id/comment-count`'s 200 — how many comments the card has, for the
 * card back's header (owner-directed, `docs/notes/card-back-header-meta.md`).
 *
 * Its own schema and its own route rather than a field on `cardDetailSchema`
 * (which ~15 routes serialise, several straight into the web's card-detail cache)
 * or a total on the stream (which is keyset-paged and deliberately carries none).
 * The number is `commentCount`'s footing on the board face exactly: every comment
 * on the card, `count(*)` — comments are hard-deleted, so there is no tombstone to
 * exclude, and the front badge and the back's header cannot disagree.
 */
export const cardCommentCountSchema = z.strictObject({
  commentCount: z
    .number()
    .int()
    .nonnegative()
    .describe(
      'Every comment on the card, exactly the number GET /cards/:id/comments pages through.',
    ),
});

export type CardCommentCount = z.infer<typeof cardCommentCountSchema>;
